/**
 * Share a link the best way this browser can: the system share sheet where there
 * is one (phones, Safari), otherwise the clipboard.
 */

export type ShareLinkOutcome = 'shared' | 'copied' | 'cancelled';

/** The browser capabilities used, injectable so tests don't patch `navigator`. */
export interface ShareCapabilities {
  share?: (data: { url: string; title?: string }) => Promise<void>;
  clipboard?: { writeText: (text: string) => Promise<void> };
}

/** How the link went out. Rejects only when it could not go out at all. */
export async function shareLink(
  data: { url: string; title?: string },
  capabilities: ShareCapabilities = navigator,
): Promise<ShareLinkOutcome> {
  if (typeof capabilities.share === 'function') {
    try {
      await capabilities.share(data);
      return 'shared';
    } catch (error) {
      // Closing the share sheet rejects with AbortError: a choice, not a failure —
      // and no reason to copy the link behind the person's back.
      if ((error as { name?: string } | null)?.name === 'AbortError') return 'cancelled';
      // Anything else (sharing not allowed in this frame, say): the clipboard may still work.
    }
  }
  if (!capabilities.clipboard) {
    throw new Error('This browser can neither share nor copy a link.');
  }
  await capabilities.clipboard.writeText(data.url);
  return 'copied';
}
